/**
 * Routes a fenced code block to the component that renders it.
 *
 * The agent writes narrative Markdown with fenced blocks in it — ```highcharts,
 * ```brand-kpi-grid and so on — and each one is a widget rather than code. See
 * docs/output_visual_blocks.md in the agent repo for the payload of each kind.
 *
 * Three things this has to survive, because all of them happen normally:
 *
 * - **A block still streaming.** Text arrives a few characters at a time, so a
 *   fence is incomplete for as long as it takes to write. Parsing it early
 *   would flash an error on every block; it renders as plain text until the
 *   closing fence lands.
 * - **Malformed JSON.** The sender is a model. A block that will not parse
 *   falls back to showing the code, which is more use than an error.
 * - **A kind this build does not know.** New block types ship on the agent's
 *   schedule, not the UI's, so anything unrecognised renders as code.
 * - **A backticked word.** Markdown uses the same element for `a term` inside a
 *   sentence and for a whole fenced block, so this has to tell them apart.
 */

import React from "react";
import PropTypes from "prop-types";
import CodeBlock from "../CodeBlock";
import ChartBlock from "../ChartBlock";
import BlockBoundary from "./BlockBoundary";
import BrandKpiGrid from "./BrandKpiGrid";
import ConversionFunnel from "./ConversionFunnel";
import DiagnosticCard from "./DiagnosticCard";
import RecommendationDeck from "./RecommendationDeck";
import { looksLikeParams } from "../../utils/appliedParams";
import MermaidBlock from "./MermaidBlock";
import BlockSkeleton from "./BlockSkeleton";
import { looksLikeStatistic } from "./InlineText";
import "./VisualBlock.scss";

/** Every kind this build renders, and what it renders with. */
const RENDERERS = {
  highcharts: (data) => <ChartBlock highchartsConfig={data} themed />,
  "brand-kpi-grid": (data) => <BrandKpiGrid {...data} />,
  "conversion-funnel": (data) => <ConversionFunnel {...data} />,
  "diagnostic-card": (data) => <DiagnosticCard {...data} />,
  "recommendation-cards": (data) => <RecommendationDeck {...data} />,
  // Handled in their own branches below rather than here: the metadata kinds
  // are hidden, and `mermaid` is not JSON at all.
  // They still need an entry — anything absent from this map is shown as code.
  "response-metadata": () => null,
  json: () => null,
  mermaid: () => null,
};

/** `language-highcharts` -> `highcharts`. */
const kindOf = (className = "") =>
  /language-([\w-]+)/.exec(className)?.[1]?.toLowerCase() ?? "";

/**
 * Whether a fence looks finished.
 *
 * A JSON block is complete when its braces balance. Counting them is enough
 * here and costs nothing — the alternative is parsing on every keystroke of a
 * streaming reply and discarding the failures.
 */
const looksComplete = (text) => {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (const ch of trimmed) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === "\\") {
      escaped = true;
      continue;
    }
    if (ch === '"') inString = !inString;
    if (inString) continue;
    if (ch === "{" || ch === "[") depth += 1;
    if (ch === "}" || ch === "]") depth -= 1;
  }
  return depth === 0;
};

/**
 * Whether this is a backticked word in a sentence rather than a fenced block.
 *
 * react-markdown dropped the `inline` prop it used to pass, so the distinction
 * has to be read from the source positions it still gives: a fence opens and
 * closes on different lines, a backticked word does not. The prop is still
 * honoured first in case it ever returns.
 */
const isSingleLine = (node, text) => {
  const { start, end } = node?.position ?? {};
  if (typeof start?.line === "number" && typeof end?.line === "number") {
    return start.line === end.line;
  }
  return !/\n/.test(text);
};

const VisualBlock = ({ inline, node, className, children, ...rest }) => {
  const text = String(children ?? "");
  const kind = kindOf(className);
  const render = RENDERERS[kind];

  // A term quoted mid-sentence belongs in that sentence. Sent to CodeBlock it
  // became a full-width slab that broke the paragraph around it. A language
  // class means a fence regardless of lines, which keeps a just-opened block
  // from flickering as inline while it streams.
  if (!kind && (inline ?? isSingleLine(node, text))) {
    // The agent backticks its statistics (`* p < 0.05`, `ns, n = 4,931`) because
    // its prompt does. They are part of the sentence, not code, so they read
    // as text; a backticked identifier still gets its pill.
    if (looksLikeStatistic(text)) return text;
    return <code className="lou-inlineCode">{text}</code>;
  }

  // Metadata is for the "What Lou knew" panel only; nothing renders in the reply.
  if (kind === "response-metadata") return null;

  // Anything this build has no renderer for still shows as code.
  if (!render) {
    return <CodeBlock language={kind} code={text} {...rest} />;
  }

  // Mermaid is a diagram language, not JSON, so it has no braces to balance:
  // a closing fence is the only signal it is finished. `children` still
  // growing means more is coming.
  if (kind === "mermaid") {
    return (
      <BlockBoundary kind={kind}>
        <div className="lou-block lou-block--mermaid">
          <MermaidBlock code={text} />
        </div>
      </BlockBoundary>
    );
  }

  // Still arriving. A placeholder shaped like the block, never its raw JSON —
  // watching code appear in a conversation and then vanish reads as a fault,
  // and the fence has already named what is coming.
  if (!looksComplete(text)) {
    return (
      <div className={`lou-block lou-block--${kind}`}>
        <BlockSkeleton kind={kind} />
      </div>
    );
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    console.warn(`[Lou] ${kind} block is not valid JSON`, text);
    return (
      <p className="lou-block__failed">
        This {kind.replace(/-/g, " ")} could not be displayed.
      </p>
    );
  }

  if (kind === "json") {
    return looksLikeParams(data) ? null : (
      <CodeBlock language={kind} code={text} {...rest} />
    );
  }

  return (
    <BlockBoundary kind={kind}>
      <div className={`lou-block lou-block--${kind}`}>{render(data)}</div>
    </BlockBoundary>
  );
};

VisualBlock.propTypes = {
  inline: PropTypes.bool,
  node: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default VisualBlock;
